import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoaderCircle } from 'lucide-react';
import * as yup from 'yup';

import { useCheckUserStatus } from '@/api/auth/useCheckUserStatus';
import { useResendVerificationEmail } from '@/api/auth/useResendVerificationEmail';
import { brandColors } from '@/app/constants';
import AuthLayout from '@/component-library/layouts/AuthLayout/AuthLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import ArrowIcon from '@/component-library/primitives/Icons/ArrowIcon/ArrowIcon';
import Link from '@/component-library/primitives/Link/Link';
import { Input } from '@/component-library/widgets/Input/Input';
import { ForgotPasswordData } from '@/models/ForgotPasswordData';
import { AppRoutes } from '@/utils/routes/router';

import { useAuth } from '../../hooks/useAuth';

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const checkUserStatusMutation = useCheckUserStatus();
  const resendVerificationEmailMutation = useResendVerificationEmail();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const { t } = useTranslation();
  const [attemptLimitExceeded, setAttemptLimitExceeded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - Type instantiation depth issue with i18next
        .email(t('CommonErrors.emailFormatValidation'))
        .max(256)
        .required(t('CommonErrors.emailRequired')),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async ({ email }: ForgotPasswordData) => {
    setIsLoading(true);
    // Clear any previous error messages
    setErrorMessage('');
    setAttemptLimitExceeded(false);

    try {
      // Check user status first to prevent unnecessary Cognito API calls
      const userStatusResult = await checkUserStatusMutation.mutateAsync({ email });

      // If user cannot reset password (FORCE_CHANGE_PASSWORD state), resend verification email and redirect to verify-email
      if (!userStatusResult.canResetPassword) {
        try {
          // Automatically resend the verification email with temporary password
          await resendVerificationEmailMutation.mutateAsync({ email });
        } catch (resendError: any) {
          // Log error but still redirect - user can manually resend from verify-email page
          console.error('Failed to resend verification email:', resendError);
        }
        setIsLoading(false);
        // Pass a flag indicating user came from forgot-password flow
        navigate(`${AppRoutes.verifyEmail}?email=${encodeURIComponent(email)}&fromForgotPassword=true`, {
          replace: true,
        });
        return;
      }

      // User can reset password - proceed with forgotPassword
      await forgotPassword(email);
      navigate(AppRoutes.resetPassword);
    } catch (error: unknown) {
      // Handle errors from checkUserStatus
      const axiosError = error as { response?: { status?: number }; code?: string };
      if (axiosError?.response?.status === 404) {
        setErrorMessage('User not found. Please check the email address.');
      } else if (axiosError.code === 'LimitExceededException') {
        setAttemptLimitExceeded(true);
      } else {
        // If checkUserStatus fails (network error, endpoint not deployed, etc.),
        // redirect to verify-email as a safe default rather than trying forgotPassword
        // This prevents the Cognito API call from failing for users in FORCE_CHANGE_PASSWORD state
        setIsLoading(false);
        // Pass a flag indicating user came from forgot-password flow
        navigate(`${AppRoutes.verifyEmail}?email=${encodeURIComponent(email)}&fromForgotPassword=true`, {
          replace: true,
        });
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <AuthLayout content={t('ForgotPasswordPage.subtitle')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full space-y-6">
        <Input
          placeholder={t('ForgotPasswordPage.emailPlaceholder')}
          {...register('email')}
          label={t('ForgotPasswordPage.emailLabel')}
          error={errors.email?.message}
          data-cy="userEmail"
        />

        <Button
          className="w-full bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
          data-cy="buttonSubmit"
          disabled={isLoading}
        >
          {t('ForgotPasswordPage.buttonSubmit')} {isLoading && <LoaderCircle height={20} className="animate-spin" />}
        </Button>

        {attemptLimitExceeded && (
          <p className="text-custom-red text-xs font-medium">{t('ResetPasswordPage.attemptLimitExceeded')}</p>
        )}
        {errorMessage && <p className="text-custom-red text-xs font-medium">{errorMessage}</p>}

        <div className="flex justify-end mt-2">
          <Link
            to={AppRoutes.signIn}
            variant="primary"
            label={t('ForgotPasswordPage.linkBackToSignin')}
            iconLeft={<ArrowIcon type="left" color={brandColors.blue} width={16} />}
            className="text-sm text-custom-blue hover:text-custom-darkBlue transition-colors no-underline"
          />
        </div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
